import { useEffect, useState } from "react";
import { Settings, Sparkles } from "lucide-react";
import QuickStatCard from "../../../components/marketing/cloud-dashboard/quick-stat";
import Channel from "../../../components/marketing/cloud-dashboard/channel";
import CommunicationChannels from "../../../components/marketing/cloud-dashboard/channel";
import ConvoHubPlatformModal from "../cpas-platform";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserResponse } from "../../../components/types";
import useRequest from "../../../components/hook/use-req";

interface Channel {
  id: string;
  label: string;
  status: 'active' | 'setup' | 'inactive';
  path: string;
}

const CPaaSDashboard = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [createProfileModal, setCreateProfileModal] = useState(true);
  const [showPlatform, setShowPlatform] = useState(false);
  const [user, setUser] = useState<UserResponse | null>(null);
  // Seeded from localStorage (set at login) so the CPaaS hub picker knows
  // the user's access level immediately, instead of showing everything as
  // restricted while /auth/get_userdata is still in flight.
  const [role, setRole] = useState<string>(() => localStorage.getItem("role") || "");
  const [searchParams] = useSearchParams();
  const firstNameParam = searchParams.get("first_name");
  const [stats] = useState({
    total_messages: 0,
    campaign_messages: 0,
    transactional_messages: 0,
    total_voice_calls: 0,
  });

  useEffect(() => {
    const hashToken = window.location.hash.replace("#", "");
    const storedToken = localStorage.getItem("authToken");

    if (hashToken) {
      localStorage.setItem("authToken", hashToken);
      setToken(hashToken);
    } else if (storedToken) {
      setToken(storedToken);
    } else {
      navigate("/login"); // redirect properly
    }
  }, [navigate]);

  const { makeRequest: getUserData } = useRequest(
    `/auth/get_userdata`,
    "GET",
    { Authorization: `Bearer ${token}` }
  );

  useEffect(() => {
    if (firstNameParam) {
      setCreateProfileModal(true);
      setShowPlatform(false);
      return;
    }
    setCreateProfileModal(false);

    // Per direct ask: the suite picker always shows here now — only
    // global_admin and reseller skip it, and they do that earlier, in
    // login.tsx's own post-login redirect (they never land on /cpas at all).
    // The old "remember my choice and skip next time" shortcut (localStorage
    // "defaultSuite") has been removed, including its checkbox in
    // cpas-platform/index.tsx.
    setShowPlatform(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstNameParam]);

  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      const [response] = await getUserData();
      setUser(response);
      localStorage.setItem("logoIcon", response?.payload?.org_logo);
      if (response?.payload?.role) {
        setRole(response.payload.role);
        localStorage.setItem("role", response.payload.role);
      }
      // org_role is what actually distinguishes a business_user's
      // permission level — see sidebar/index.tsx's CRM-access gate.
      if (response?.payload?.org_role) {
        localStorage.setItem("org_role", response.payload.org_role);
      }
    };

    fetchData();
  }, [token]);



  const [channels] = useState<Channel[]>([
    { id: 'sms', label: 'SMS', status: 'active', path: '/dashboard' },
    { id: 'whatsapp', label: 'WhatsApp', status: 'active', path: '/whatsapp-setup' },
    { id: 'email', label: 'Email', status: 'active', path: '/email' },
    { id: 'rcs', label: 'RCS', status: 'setup', path: '/rcs-setup' },
    { id: 'voice', label: 'Voice', status: 'active', path: '/voice' },
    { id: 'webchat', label: 'Web Chat', status: 'setup', path: '/webchat-setup' },
    { id: 'telegram', label: 'Telegram', status: 'inactive', path: '/telegram-setup' }
  ]);

  const handleChannelClick = (channelId: string) => {

    // Handle channel click logic here
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 shadow-sm sticky top-0 ">
        <div className="mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                  ConvoHub
                </h1>
                <p className="text-sm text-slate-500">
                  AI-Powered Communication Platform
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <Settings className="w-5 h-5 text-slate-600" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className=" mx-auto px-6 py-8">
        {/* Hero Section */}
        <div className="mb-12">
          {/* Quick Stats */}
          <QuickStatCard {...stats} />
        </div>

        <CommunicationChannels
          channels={channels}
          onChannelClick={handleChannelClick}
        />
      </div>

      <ConvoHubPlatformModal
        isOpen={showPlatform}
        onClose={() => setShowPlatform(false)}
        token={token ?? undefined}
        role={role}
        onNavigate={(path) => {
          setShowPlatform(false);
          navigate(path);
        }}
      />
    </div>
  );
};

export default CPaaSDashboard;
